import { useEffect, useState } from 'react';
import { endpoints, type SsoSettings } from '../api';
import { Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, Status, dateTime } from '../components';
import { useApiQuery } from '../hooks';

export function AdminPage({ isAdmin = false, canUseApp = true }: { isAdmin?: boolean; canUseApp?: boolean } = {}) {
  const status = useApiQuery(endpoints.adminStatus);

  return <Page title="Administración" actions={<Button variant="ghost" onClick={() => void status.reload()}>Comprobar</Button>}>
    {!canUseApp && <Note>Configura EVE SSO y el acceso de Kharvex Armaments. Después conecta tu piloto para entrar en tu espacio personal. Cada miembro de la corporación podrá iniciar sesión con su propia cuenta de EVE.</Note>}
    <Section title="Salud del sistema">
      {status.loading && !status.data && <LoadingState />}
      {status.error && <ErrorState error={status.error} onRetry={() => void status.reload()} />}
      {status.data && <>
        <div className="table-wrap"><table className="table--stack"><thead><tr><th>Servicio</th><th>Estado</th><th>Detalle</th><th>Último éxito</th></tr></thead><tbody>
          <Service name="Base de datos" {...status.data.database} />
          <Service name="EVE ESI" {...status.data.esi} />
          <Service name="Planificador" {...status.data.scheduler} />
        </tbody></table></div>
        <dl className="meta">
          <div><dt>Versión</dt><dd>{status.data.version}</dd></div>
          <div><dt>Entorno</dt><dd>{status.data.environment}</dd></div>
          <div><dt>Última sincronización</dt><dd>{dateTime(status.data.lastSyncAt)}</dd></div>
        </dl>
      </>}
    </Section>
    {status.data && <div className={isAdmin ? 'split split--even' : undefined}>
      <Section title="Datos estáticos (SDE)">
        {!status.data.staticData?.version ? <EmptyState title="Sin importación verificada" detail=" Hace falta para recetas y requisitos." /> : <dl className="rows">
          <div><dt>Versión</dt><dd>{status.data.staticData.version}</dd></div>
          <div><dt>Estado</dt><dd className="wrap"><Status value={status.data.staticData.status ?? 'unknown'} /></dd></div>
          <div><dt>Importación</dt><dd>{adminDate(status.data.staticData.importedAt)}</dd></div>
          <div><dt>SHA-256</dt><dd>{status.data.staticData.sha256 ?? 'Sin verificar'}</dd></div>
          <div><dt>Fuente</dt><dd className="wrap">{status.data.staticData.source_url ? <a className="link" href={status.data.staticData.source_url} target="_blank" rel="noreferrer">{status.data.staticData.source_url}</a> : 'Sin registrar'}</dd></div>
        </dl>}
      </Section>
      {isAdmin && <Section title="Copias y restauración">
        <dl className="rows">
          <div><dt>Última copia</dt><dd>{adminDate(status.data.backup?.lastSuccess)}</dd></div>
          <div><dt>Cifrado</dt><dd>{status.data.backup?.encrypted === undefined ? 'Sin verificar' : status.data.backup.encrypted ? 'Sí' : 'No'}</dd></div>
          <div><dt>Prueba de restauración</dt><dd className="wrap"><Status value={status.data.backup?.restoreValidated ? 'validada' : 'pendiente'} /></dd></div>
          <div><dt>Última restauración validada</dt><dd>{adminDate(status.data.backup?.lastRestoreValidation)}</dd></div>
        </dl>
      </Section>}
    </div>}
    {isAdmin && <SsoConfiguration />}
    {canUseApp && <WandererAccessCard />}
  </Page>;
}

function SsoConfiguration() {
  const sso = useApiQuery(endpoints.sso);
  const [form, setForm] = useState<SsoSettings | null>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);
  useEffect(() => { if (sso.data) setForm(sso.data); }, [sso.data]);

  function toggleScope(scope: string) {
    setForm((current) => current ? { ...current, scopes: current.scopes.includes(scope) ? current.scopes.filter((item) => item !== scope) : [...current.scopes, scope] } : current);
  }

  async function saveSso(event: React.FormEvent) {
    event.preventDefault();
    if (!form?.clientId) return;
    setBusy(true); setMessage(null);
    try {
      const saved = await endpoints.saveSso({ clientId: form.clientId, ...(clientSecret ? { clientSecret } : {}), callbackUrl: form.callbackUrl, scopes: form.scopes });
      setForm(saved); setClientSecret(''); setMessage({ tone: 'success', text: 'EVE SSO guardado. Los miembros de Kharvex Armaments ya pueden iniciar sesión cuando esté configurada la corporación admitida. Pulsa «Conectar piloto» para abrir tu espacio personal.' });
    } catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo guardar' }); }
    finally { setBusy(false); }
  }

  async function connectPilot() {
    setMessage(null);
    try { const result = await endpoints.startEveAuth(); window.location.assign(result.authorizeUrl); }
    catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo iniciar SSO' }); }
  }

  return <Section title="EVE SSO">
      {message && <Note tone={message.tone}>{message.text}</Note>}
      {sso.loading && !sso.data && <LoadingState />}
      {sso.error && <ErrorState error={sso.error} onRetry={() => void sso.reload()} />}
      {form && <form className="form-stack" onSubmit={saveSso} autoComplete="off">
        <p className="fine">Registra una <a className="link" href="https://developers.eveonline.com/applications" target="_blank" rel="noreferrer">aplicación de CCP ↗</a> con esta URL de retorno. Con PKCE solo necesitas el Client ID. El acceso está reservado a miembros de Kharvex Armaments; cada piloto mantiene sus propios datos.</p>
        <div className="form-grid">
          <Field label="Client ID"><input required value={form.clientId ?? ''} onChange={(event) => setForm({ ...form, clientId: event.target.value })} /></Field>
          <Field label="URL de retorno"><input required readOnly value={form.callbackUrl} type="url" /></Field>
        </div>
        <details><summary>Permisos solicitados · {form.scopes.length}</summary><fieldset className="stack" style={{ gap: 4 }}><legend className="sr-only">Permisos solicitados</legend>{(form.availableScopes ?? form.scopes).map((scope) => <label className="check mono" key={scope}><input type="checkbox" checked={form.scopes.includes(scope)} onChange={() => toggleScope(scope)} /><span>{scope}</span></label>)}</fieldset></details>
        <details><summary>Secreto opcional</summary><Field label="Client secret" hint="No hace falta con PKCE. Vacío conserva el valor anterior."><input value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} type="password" autoComplete="new-password" /></Field></details>
        <div className="actions"><Button disabled={busy}>{busy ? 'Guardando…' : 'Guardar SSO'}</Button><Button type="button" variant="secondary" disabled={!form.configured} onClick={() => void connectPilot()}>Conectar piloto ↗</Button></div>
      </form>}
    </Section>;
}

function WandererAccessCard() {
  const access = useApiQuery(endpoints.wandererAccess);
  const urls = useApiQuery(endpoints.accessSettings);
  const [entry, setEntry] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setMessage(null);
    try { await endpoints.saveWandererAccess(entry); setEntry(''); await access.reload(); await urls.reload(); setMessage({ tone: 'success', text: 'Entrada privada guardada. Ya puedes entrar desde Espacio → Agujeros de gusano.' }); }
    catch (error) { setMessage({ tone: 'error', text: error instanceof Error ? error.message : 'No se pudo guardar el enlace' }); }
    finally { setBusy(false); }
  }
  return <Section title="Entrada privada de Wanderer">
    <p className="fine">Guarda tu invitación privada de tipo «User» para entrar en Wanderer. Esta entrada pertenece a tu piloto y se almacena cifrada en tu espacio personal.</p>
    {access.error && <ErrorState error={access.error} onRetry={() => void access.reload()} />}
    {access.data?.entryConfigured && <Note>Hay una entrada guardada. Su validez la comprueba Wanderer al entrar; revócala desde su administración cuando no la necesites.</Note>}
    <form className="form-stack" onSubmit={save} autoComplete="off">
      <Field label="URL privada de invitación" hint="No la compartas."><input required type="password" autoComplete="off" value={entry} onChange={(event) => setEntry(event.target.value)} /></Field>
      {message && <Note tone={message.tone}>{message.text}</Note>}
      <div className="actions"><Button disabled={busy}>{busy ? 'Guardando…' : 'Guardar entrada'}</Button>{urls.data?.wandererEntryUrl && <a className="button" href={urls.data.wandererEntryUrl} target="_blank" rel="noreferrer">Entrar en Wanderer ↗</a>}</div>
    </form>
  </Section>;
}

function Service({ name, status, detail, lastSuccessAt }: { name: string; status: string; detail: string | null; lastSuccessAt: string | null }) {
  return <tr>
    <td className="wide"><strong>{name}</strong></td>
    <td data-label="Estado"><Status value={status} /></td>
    <td className="wide muted" data-label="Detalle">{detail ?? '—'}</td>
    <td className="muted" data-label="Último éxito" style={{ whiteSpace: 'nowrap' }}>{dateTime(lastSuccessAt)}</td>
  </tr>;
}

function adminDate(value?: string | number | null) {
  return dateTime(typeof value === 'number' ? new Date(value * 1000).toISOString() : value);
}
